import { viewValues } from './../stats.config';
import { Component, effect, EventEmitter, OnDestroy, Output, signal, WritableSignal } from '@angular/core';
import { Subscription, tap } from 'rxjs';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { matImports, ngImports } from '@app/shared/imports';
import { StatsTransferService } from '../stats-transfer.service';
import { VarDirective } from '@app/shared/directives';
import { StatsOptionsForm, StatsOptions, StatsGraphType, StatsActionType } from '../models';
import { datepickerFormats, seriesUnitOptions } from './stats-fields-form.config';

@Component({
  selector: 'app-stats-fields-form',
  templateUrl: './stats-fields-form.component.html',
  styleUrls: ['./stats-fields-form.component.scss'],
  standalone: true,
  imports: [ ...ngImports, ...matImports, VarDirective ],
  providers: [
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
    { provide: MAT_DATE_FORMATS, useValue: datepickerFormats },
  ]
})
export class StatsFieldsFormComponent implements OnDestroy {
  @Output() valueChange: EventEmitter<StatsOptions> = new EventEmitter<StatsOptions>();

  private subscriptions = new Subscription();

  readonly graphType = StatsGraphType;
  readonly unitOptions = seriesUnitOptions;
  readonly viewValues = viewValues;

  formReady: WritableSignal<boolean> = signal(false);

  aggFunctionOptions: WritableSignal<string[]> = signal([]);
  form: StatsOptionsForm;

  constructor(
    public transfer: StatsTransferService
  ) {
    effect(() => {
      if (this.transfer.formData()) {
        this.subscriptions.unsubscribe();
        this.subscriptions = new Subscription();
        this.form = this.transfer.form;
        const valueChanges$ = this.form.valueControl.valueChanges.pipe(
          tap(value => this.onValueControlChange(value))
        );
        this.subscriptions.add(valueChanges$.subscribe());
        this.onValueControlChange(this.form.valueControl.value);

        this.formReady.set(true);
        return;
      }

      this.formReady.set(false);
    }, { allowSignalWrites: true });
  }

  onValueControlChange(value: string): void {
    this.aggFunctionOptions.set(
      this.transfer.formData().valueOptions.find(field => field.field === value)?.aggFunction
    );

    if (this.aggFunctionOptions().length >= 1) {
      this.form.patchValue({ aggFunction: this.aggFunctionOptions()[0] });
    }
  }

  onClickCheckbox(controlName: string): void {
    const control = this.form.filter.get(controlName);
    if (!control) {
      return;
    }

    const value = control.value;

    if (value === true) {
      control.setValue(false);
    } else if (value === false) {
      control.setValue(null);
    } else {
      control.setValue(true);
    }

    control.markAsDirty();
  }

  onClickResetDates(): void {
    this.form.patchValue({ startDate: null, endDate: null });
    this.form.markAsDirty();
  }

  onSaveForm(): void {
    this.transfer.sharedSubject.next({ type: StatsActionType.FORM_SUCCESS });
  }

  onResetForm(): void {
    this.transfer.sharedSubject.next({ type: StatsActionType.FORM_RESET });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
